import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import './accounting.css';

const Vouchers = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    account_id: '',
    payment_method: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchVouchers();
    fetchStatistics();
  }, [filters]);

  const fetchVouchers = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://naql.nozzm.com/api/accounting/vouchers', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
      });

      const result = await response.json();
      if (result.status) {
        setVouchers(result.data.vouchers.data || []);
        setStatistics(result.data.statistics || {});
      } else {
        console.error('Failed to fetch vouchers:', result.message);
      }
    } catch (error) {
      console.error('Error fetching vouchers:', error);
    } finally {
      setLoading(false);
    }
  };



  const fetchStatistics = async () => {
    try {
      const response = await fetch('https://naql.nozzm.com/api/accounting/vouchers/statistics', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
      });

      const result = await response.json();
      if (result.status) {
        setStatistics(result.data.statistics || {});
      } else {
        console.error('Failed to fetch statistics:', result.message);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleAddVoucher = () => {
    navigate('/vouchers/add');
  };

  const handleEditVoucher = (id) => {
    navigate(`/vouchers/edit/${id}`);
  };

  const handleViewVoucher = (id) => {
    navigate(`/vouchers/view/${id}`);
  };

  const handlePost = async (id) => {
    try {
      const response = await fetch(`https://naql.nozzm.com/api/accounting/vouchers/${id}/post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify({}),
      });

      const result = await response.json();
      if (result.status) {
        fetchVouchers();
      } else {
        console.error('Failed to post voucher:', result.message);
      }
    } catch (error) {
      console.error('Error posting voucher:', error);
    }
  };

  const handleCancel = async (id) => {
    try {
      const response = await fetch(`https://naql.nozzm.com/api/accounting/vouchers/${id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify({}),
      });

      const result = await response.json();
      if (result.status) {
        fetchVouchers();
      } else {
        console.error('Failed to cancel voucher:', result.message);
      }
    } catch (error) {
      console.error('Error cancelling voucher:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('accounting.vouchers.actions.delete'))) {
      try {
        const response = await fetch(`https://naql.nozzm.com/api/accounting/vouchers/${id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'lang': i18n.language,
          },
        });

        const result = await response.json();
        if (result.status) {
          fetchVouchers();
        } else {
          console.error('Failed to delete voucher:', result.message);
        }
      } catch (error) {
        console.error('Error deleting voucher:', error);
      }
    }
  };

  return (
    <div className="vouchers-container">
      <div className="page-header">
        <h1>{t('accounting.vouchers.title')}</h1>
        <button className="btn btn-primary" onClick={handleAddVoucher}>
          {t('accounting.vouchers.addVoucher')}
        </button>
      </div>

      {/* Statistics */}
      <div className="statistics-grid">
        <div className="stat-card">
          <h3>{t('accounting.vouchers.statistics.totalVouchers')}</h3>
                     <p>{statistics?.total_vouchers || 0}</p>
        </div>
        <div className="stat-card">
          <h3>{t('accounting.vouchers.statistics.receiptsCount')}</h3>
                     <p>{statistics?.receipts_count || 0}</p>
        </div>
        <div className="stat-card">
          <h3>{t('accounting.vouchers.statistics.paymentsCount')}</h3>
                     <p>{statistics?.payments_count || 0}</p>
        </div>
        <div className="stat-card">
          <h3>{t('accounting.vouchers.statistics.totalReceiptsAmount')}</h3>
                     <p>{statistics?.total_receipts_amount || 0} SAR</p>
        </div>
        <div className="stat-card">
          <h3>{t('accounting.vouchers.statistics.totalPaymentsAmount')}</h3>
                     <p>{statistics?.total_payments_amount || 0} SAR</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          <input
            type="text"
            placeholder={t('accounting.vouchers.voucherNumber')}
            value={filters.search}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
          />
          <select
            value={filters.type}
            onChange={(e) => setFilters({...filters, type: e.target.value})}
          >
            <option value="">{t('accounting.vouchers.type')}</option>
            <option value="receipt">{t('accounting.vouchers.voucherTypes.receipt')}</option>
            <option value="payment">{t('accounting.vouchers.voucherTypes.payment')}</option>
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
          >
            <option value="">{t('accounting.vouchers.status')}</option>
            <option value="draft">{t('accounting.vouchers.voucherStatus.draft')}</option>
            <option value="posted">{t('accounting.vouchers.voucherStatus.posted')}</option>
            <option value="cancelled">{t('accounting.vouchers.voucherStatus.cancelled')}</option>
          </select>
          <select
            value={filters.payment_method}
            onChange={(e) => setFilters({...filters, payment_method: e.target.value})}
          >
            <option value="">{t('accounting.vouchers.paymentMethod')}</option>
            <option value="cash">{t('accounting.vouchers.paymentMethods.cash')}</option>
            <option value="bank_transfer">{t('accounting.vouchers.paymentMethods.bank_transfer')}</option>
            <option value="check">{t('accounting.vouchers.paymentMethods.check')}</option>
            <option value="card">{t('accounting.vouchers.paymentMethods.card')}</option>
          </select>
          <input
            type="date"
            value={filters.start_date}
            onChange={(e) => setFilters({...filters, start_date: e.target.value})}
          />
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) => setFilters({...filters, end_date: e.target.value})}
          />
        </div>
      </div>

      {/* Vouchers Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('accounting.vouchers.voucherNumber')}</th>
                <th>{t('accounting.vouchers.type')}</th>
                <th>{t('accounting.vouchers.voucherDate')}</th>
                <th>{t('accounting.vouchers.account')}</th>
                <th>{t('accounting.vouchers.amount')}</th>
                <th>{t('accounting.vouchers.paymentMethod')}</th>
                <th>{t('accounting.vouchers.status')}</th>
                <th>{t('accounting.vouchers.actions.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {vouchers.map(voucher => (
                <tr key={voucher.id}>
                  <td>{voucher.voucher_number}</td>
                  <td>
                    <span className={`badge ${voucher.type === 'receipt' ? 'badge-success' : 'badge-warning'}`}>
                      {t(`accounting.vouchers.voucherTypes.${voucher.type}`)}
                    </span>
                  </td>
                  <td>{voucher.voucher_date}</td>
                  <td>{voucher.account?.name}</td>
                  <td>{voucher.amount} {voucher.currency}</td>
                  <td>{t(`accounting.vouchers.paymentMethods.${voucher.payment_method}`)}</td>
                  <td>
                    <span className={`badge badge-${voucher.status === 'posted' ? 'success' : voucher.status === 'draft' ? 'warning' : 'danger'}`}>
                      {t(`accounting.vouchers.voucherStatus.${voucher.status}`)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => handleViewVoucher(voucher.id)}
                        title={t('accounting.vouchers.actions.view')}
                      >
                        👁️
                      </button>
                      {voucher.status === 'draft' && (
                        <>
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => handleEditVoucher(voucher.id)}
                            title={t('accounting.vouchers.actions.edit')}
                          >
                            ✏️
                          </button>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handlePost(voucher.id)}
                            title={t('accounting.vouchers.actions.post')}
                          >
                            ✅
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(voucher.id)}
                            title={t('accounting.vouchers.actions.delete')}
                          >
                            🗑️
                          </button>
                        </>
                      )}
                      {voucher.status === 'posted' && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleCancel(voucher.id)}
                          title={t('accounting.vouchers.actions.cancel')}
                        >
                          ❌
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Vouchers;
